import type {
	UseSearchKeyboardNavOptions,
	UseSearchKeyboardNavReturn,
} from "@workspace/types";
import { useCallback, useEffect } from "react";

/**
 * Keyboard navigation hook for search results
 *
 * Handles:
 * - Arrow key navigation (up/down)
 * - Enter key selection
 * - Escape key to close
 *
 * @param options - Navigation options including total results and callbacks
 * @returns Keyboard navigation state and handlers
 */
export function useSearchKeyboardNav({
	totalResults,
	selectedIndex,
	onSelectedIndexChange,
	onSelect,
	onClose,
}: UseSearchKeyboardNavOptions): UseSearchKeyboardNavReturn {
	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			switch (e.key) {
				case "ArrowUp":
					e.preventDefault();
					if (totalResults > 0) {
						onSelectedIndexChange(
							selectedIndex <= 0 ? totalResults - 1 : selectedIndex - 1,
						);
					}
					break;

				case "ArrowDown":
					e.preventDefault();
					if (totalResults > 0) {
						onSelectedIndexChange(
							selectedIndex >= totalResults - 1 ? 0 : selectedIndex + 1,
						);
					}
					break;

				case "Enter":
					e.preventDefault();
					if (selectedIndex >= 0 && selectedIndex < totalResults) {
						// Select the currently highlighted result
						onSelect(selectedIndex);
					} else if (selectedIndex === -1 && totalResults > 0) {
						// No result selected but results exist - select the first one
						onSelect(0);
					}
					break;

				case "Escape":
					e.preventDefault();
					onClose();
					break;
			}
		},
		[totalResults, selectedIndex, onSelectedIndexChange, onSelect, onClose],
	);

	const resetSelection = useCallback(() => {
		onSelectedIndexChange(-1);
	}, [onSelectedIndexChange]);

	// Reset selection when results change
	useEffect(() => {
		resetSelection();
	}, [resetSelection]);

	return {
		selectedIndex,
		handleKeyDown,
		resetSelection,
	};
}
